import React, { useRef, useEffect } from 'react'
import { DateRangePickerProps } from './types'
import { useDateRangePicker } from './useDateRangePicker'
import DateInput from './DateInput'
import CalendarTrigger from './CalendarTrigger'
import DateRangePopover from './DateRangePopover'
import Calendar from './Calendar'

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onDateRangeChange,
  timePeriod,
  onTimePeriodChange,
  className = '',
  minDate,
  maxDate,
  triggerMode = 'text',
  showDateText = true,
  workspaceId,
  selectionMode = 'free',
  persistToStorage = true
}) => {
  const inputRef = useRef<HTMLDivElement>(null)

  const {
    state,
    anchorElement,
    openPicker,
    closePicker,
    selectDate,
    confirmSelection,
    updateTimePeriod,
    setDateRange,
    setWeekSelectionMode
  } = useDateRangePicker(startDate, endDate, timePeriod, persistToStorage)

  // Update internal state when external props change (but not during week selection mode)
  useEffect(() => {
    if (!state.isWeekSelectionMode) {
      setDateRange(startDate, endDate)
    }
  }, [startDate, endDate, setDateRange, state.isWeekSelectionMode])

  // Update time period when external prop changes
  useEffect(() => {
    updateTimePeriod(timePeriod)
  }, [timePeriod, updateTimePeriod])

  const handleDateClick = (type: 'start' | 'end') => {
    if (inputRef.current) {
      openPicker(inputRef.current, type)
    }
  }

  const handleCalendarTriggerClick = () => {
    if (inputRef.current) {
      openPicker(inputRef.current, 'start')
    }
  }

  const handleRangeComplete = (newStartDate: Date, newEndDate: Date) => {
    confirmSelection(newStartDate, newEndDate)
    onDateRangeChange(newStartDate, newEndDate)
  }

  return (
    <div ref={inputRef} className={`flex items-center gap-2 ${className}`}>
      {showDateText && (
        <DateInput
          startDate={state.selectedStartDate}
          endDate={state.selectedEndDate}
          onDateClick={triggerMode === 'text' ? handleDateClick : undefined}
          timePeriod={state.timePeriod}
          readOnly={triggerMode === 'icon'}
        />
      )}

      {triggerMode === 'icon' && (
        <CalendarTrigger
          onClick={handleCalendarTriggerClick}
        />
      )}

      <DateRangePopover
        isOpen={state.isOpen}
        onClose={closePicker}
        anchorElement={anchorElement}
        position="bottom-left"
      >
        <Calendar
          selectedStartDate={state.selectedStartDate}
          selectedEndDate={state.selectedEndDate}
          onDateSelect={selectDate}
          onRangeComplete={handleRangeComplete}
          timePeriod={state.timePeriod}
          onTimePeriodChange={onTimePeriodChange}
          minDate={minDate}
          maxDate={maxDate}
          onClose={closePicker}
          setWeekSelectionMode={setWeekSelectionMode}
          workspaceId={workspaceId}
          selectionMode={selectionMode}
        />
      </DateRangePopover>
    </div>
  )
}

export default DateRangePicker